import { Code } from '@connectrpc/connect';
import type { GroupBy, PipingSystem } from '../../types/pcfExport.types';
import type { RunExportFlowOptions, RunExportFlowResult } from './pcfExportFlow';
import {
  LocalPcfCalculationPhase,
  LocalPcfGrouping,
  type LocalPcfCalculationFile,
  type LocalPcfCalculationGroupFailure,
  type LocalPcfCalculationProgress as GrpcLocalPcfCalculationProgress,
  type LocalPcfCalculationResult,
  type LocalPcfMappingCoverage,
  type LocalPickedPcfCalculationResult,
} from './gen/pcf_export_service_pb';
import {
  createExportFailure,
  type ExportFlowContext,
  type StepResult,
} from './pcfExportContext';
import { buildSystemFilter } from './pcfExportSelection';

export interface LocalPcfFile {
  readonly fileName: string;
  readonly content: Uint8Array;
  readonly sha256: string;
  /** Elements confirmed by the plugin as belonging to this PCF group. */
  readonly elementCount: number;
}

export interface LocalPcfGroupFailure {
  readonly systemName: string;
  readonly error: string;
  readonly stage?: string;
  readonly exceptionType?: string;
  readonly elementCount?: number;
  readonly elementIds?: string[];
  readonly additionalElementCount?: number;
  readonly suggestedAction?: string;
}

export interface LocalPcfMappingCoverageCounts {
  readonly projectResolved: number;
  readonly projectConfigured: number;
  readonly systemResolved: number;
  readonly systemConfiguredSlots: number;
  readonly systemSources: number;
}

export interface LocalPcfProgress {
  readonly phase: 'capture' | 'calculate';
  readonly processed: number;
  readonly total: number;
  readonly percent: number;
  readonly succeeded: number;
  readonly failed: number;
}

export interface LocalPcfCalculationOutcome {
  readonly isSuccess: boolean;
  readonly error?: string;
  readonly errorStage?: string;
  readonly runId: string;
  readonly traceId: string;
  readonly pipelineVersion: string;
  readonly durationMs: number;
  readonly elementCount: number;
  readonly snapshotCacheHit: boolean;
  readonly files: LocalPcfFile[];
  readonly groupFailures: LocalPcfGroupFailure[];
  readonly mappingCoverage: LocalPcfMappingCoverageCounts;
}

export interface LocalPickedPcfCalculationOutcome {
  readonly calculation: LocalPcfCalculationOutcome;
  readonly elementsPicked: number;
  readonly cancelled: boolean;
}

type StreamEvent<TTerminal> = {
  readonly payload:
    | { readonly case: 'progress'; readonly value: GrpcLocalPcfCalculationProgress }
    | { readonly case: 'result'; readonly value: TTerminal }
    | { readonly case: undefined; readonly value?: undefined };
};

function requireCounter(value: number, field: string): number {
  if (!Number.isInteger(value) || value < 0) {
    throw new Error(`Local PCF calculation returned invalid ${field}.`);
  }
  return value;
}

function mapPhase(phase: LocalPcfCalculationPhase): LocalPcfProgress['phase'] {
  if (phase === LocalPcfCalculationPhase.CAPTURE) {
    return 'capture';
  }
  if (phase === LocalPcfCalculationPhase.CALCULATE) {
    return 'calculate';
  }
  throw new Error('Local PCF calculation returned an invalid progress phase.');
}

function normalizeProgress(progress: GrpcLocalPcfCalculationProgress): LocalPcfProgress {
  const percent = requireCounter(progress.percent, 'progress percent');
  if (percent > 100) {
    throw new Error('Local PCF calculation returned invalid progress percent.');
  }
  return {
    phase: mapPhase(progress.phase),
    processed: requireCounter(progress.processedGroups, 'processed group count'),
    total: requireCounter(progress.totalGroups, 'total group count'),
    percent,
    succeeded: requireCounter(progress.successfulGroups, 'successful group count'),
    failed: requireCounter(progress.failedGroups, 'failed group count'),
  };
}

function normalizeFile(file: LocalPcfCalculationFile): LocalPcfFile {
  if (!file.fileName.trim() || !(file.content instanceof Uint8Array)) {
    throw new Error('Local PCF calculation returned an invalid file payload.');
  }
  const sha256 = file.sha256.trim().toLowerCase();
  if (!/^[0-9a-f]{64}$/.test(sha256)) {
    throw new Error('Local PCF calculation returned an invalid file hash.');
  }
  return {
    fileName: file.fileName,
    content: file.content,
    sha256,
    elementCount: requireCounter(file.elementCount, 'file element count'),
  };
}

function optionalText(value: string): string | undefined {
  const normalized = value.trim();
  return normalized || undefined;
}

function normalizeGroupFailure(failure: LocalPcfCalculationGroupFailure): LocalPcfGroupFailure {
  if (!failure.systemName.trim() || !failure.error.trim()
    || failure.elementIds.some((elementId) => typeof elementId !== 'string')) {
    throw new Error('Local PCF calculation returned an invalid group failure.');
  }
  return {
    systemName: failure.systemName,
    error: failure.error,
    stage: optionalText(failure.stage),
    exceptionType: optionalText(failure.exceptionType),
    elementCount: requireCounter(failure.elementCount, 'failure element count'),
    elementIds: [...failure.elementIds],
    additionalElementCount: requireCounter(
      failure.additionalElementCount,
      'additional failure element count',
    ),
    suggestedAction: optionalText(failure.suggestedAction),
  };
}

function emptyCoverage(): LocalPcfMappingCoverageCounts {
  return {
    projectResolved: 0,
    projectConfigured: 0,
    systemResolved: 0,
    systemConfiguredSlots: 0,
    systemSources: 0,
  };
}

function normalizeCoverage(
  coverage: LocalPcfMappingCoverage | undefined,
): LocalPcfMappingCoverageCounts {
  if (!coverage) {
    return emptyCoverage();
  }
  return {
    projectResolved: requireCounter(coverage.projectResolved, 'resolved project mapping count'),
    projectConfigured: requireCounter(coverage.projectConfigured, 'configured project mapping count'),
    systemResolved: requireCounter(coverage.systemResolved, 'resolved system mapping count'),
    systemConfiguredSlots: requireCounter(
      coverage.systemConfiguredSlots,
      'configured system mapping count',
    ),
    systemSources: requireCounter(coverage.systemSources, 'system mapping source count'),
  };
}

function normalizeCalculation(
  result: LocalPcfCalculationResult,
  context: ExportFlowContext,
  allowEmptySuccess: boolean,
): LocalPcfCalculationOutcome {
  if (result.runId !== context.runId || result.traceId !== context.traceId) {
    throw new Error('Local PCF calculation returned mismatched correlation identifiers.');
  }
  if (!result.pipelineVersion.trim()) {
    throw new Error('Local PCF calculation returned no pipeline version.');
  }
  const files = result.files.map(normalizeFile);
  if (result.isSuccess && files.length === 0 && !allowEmptySuccess) {
    throw new Error('Local PCF calculation reported success without files.');
  }
  return {
    isSuccess: result.isSuccess,
    error: optionalText(result.error),
    errorStage: optionalText(result.errorStage),
    runId: result.runId,
    traceId: result.traceId,
    pipelineVersion: result.pipelineVersion,
    durationMs: requireCounter(result.durationMs, 'duration'),
    elementCount: requireCounter(result.elementCount, 'element count'),
    snapshotCacheHit: result.snapshotCacheHit,
    files,
    groupFailures: result.groupFailures.map(normalizeGroupFailure),
    mappingCoverage: normalizeCoverage(result.mappingCoverage),
  };
}

async function consumeStream<TTerminal, TResult>(
  stream: AsyncIterable<StreamEvent<TTerminal>>,
  onProgress: RunExportFlowOptions['onProgress'],
  normalizeTerminal: (terminal: TTerminal) => TResult,
): Promise<TResult> {
  let terminal: TResult | undefined;
  for await (const event of stream) {
    if (event.payload.case === 'progress') {
      if (terminal !== undefined) {
        throw new Error('Local PCF calculation streamed progress after its terminal result.');
      }
      onProgress?.(normalizeProgress(event.payload.value));
      continue;
    }
    if (event.payload.case !== 'result') {
      throw new Error('Local PCF calculation returned an empty stream event.');
    }
    if (terminal !== undefined) {
      throw new Error('Local PCF calculation returned duplicate terminal results.');
    }
    terminal = normalizeTerminal(event.payload.value);
  }
  if (terminal === undefined) {
    throw new Error('Local PCF calculation ended without a terminal result.');
  }
  return terminal;
}

function calculationFailure(
  context: ExportFlowContext,
  error: unknown,
  pickMode: boolean,
): RunExportFlowResult {
  const message = error instanceof Error ? error.message : String(error ?? '');
  const code = typeof error === 'object' && error !== null && 'code' in error
    ? error.code
    : undefined;
  const cancelledMessage = /\bcancel(?:ed|led|lation)?\b/i.test(message);
  const unimplementedMessage = /\bunimplemented\b|\bnot implemented\b/i.test(message);
  const unimplementedCode = Code.Unimplemented !== undefined && code === Code.Unimplemented;
  const cancelledCode = Code.Canceled !== undefined && code === Code.Canceled;
  if (unimplementedMessage || (!cancelledMessage && unimplementedCode)) {
    return createExportFailure({
      traceId: context.traceId,
      step: 'calculate',
      error: pickMode
        ? 'This plugin build does not support local pick-mode PCF calculation. Update the PipeEX plugin to the latest version.'
        : 'This plugin build does not support local PCF calculation. Update the PipeEX plugin to the latest version.',
      extras: { versionMismatch: true },
    });
  }
  if (cancelledMessage || cancelledCode) {
    return createExportFailure({
      traceId: context.traceId,
      step: 'calculate',
      error: 'Local PCF calculation was cancelled before completion.',
    });
  }
  return createExportFailure({
    traceId: context.traceId,
    step: 'calculate',
    error: message || 'Local PCF calculation failed.',
  });
}

function mapGrouping(groupBy: GroupBy): LocalPcfGrouping {
  if (groupBy === 'Parameter') {
    return LocalPcfGrouping.BY_PARAMETER;
  }
  if (groupBy === 'SystemTypeName') {
    return LocalPcfGrouping.BY_SYSTEM_TYPE_NAME;
  }
  return LocalPcfGrouping.BY_SYSTEM_NAME;
}

export async function calculateDocumentPcf(
  context: ExportFlowContext,
  systems: PipingSystem[],
  options: RunExportFlowOptions,
): Promise<StepResult<LocalPcfCalculationOutcome>> {
  try {
    const stream = context.client.calculateLocalPcf({
      runId: context.runId,
      traceId: context.traceId,
      grouping: mapGrouping(options.groupBy),
      settings: context.settings.grpc,
      systemNames: buildSystemFilter(systems, options.groupBy, options.groupFilter),
      includeLinkedModels: options.includeLinkedModels ?? false,
    }, {
      headers: { traceparent: context.traceparent },
    });
    const value = await consumeStream<LocalPcfCalculationResult, LocalPcfCalculationOutcome>(
      stream,
      options.onProgress,
      (terminal) => normalizeCalculation(terminal, context, false),
    );
    return { ok: true, value };
  } catch (error) {
    return { ok: false, failure: calculationFailure(context, error, false) };
  }
}

function normalizePickedCalculation(
  result: LocalPickedPcfCalculationResult,
  context: ExportFlowContext,
): LocalPickedPcfCalculationOutcome {
  if (!result.calculation) {
    throw new Error('Local picked PCF calculation returned no calculation result.');
  }
  const calculation = normalizeCalculation(result.calculation, context, result.cancelled);
  const elementsPicked = requireCounter(result.elementsPicked, 'picked element count');
  if (result.cancelled && (!calculation.isSuccess || calculation.files.length > 0)) {
    throw new Error('Local picked PCF calculation returned an invalid cancellation result.');
  }
  return { calculation, elementsPicked, cancelled: result.cancelled };
}

export async function calculatePickedPcf(
  context: ExportFlowContext,
  options: RunExportFlowOptions,
): Promise<StepResult<LocalPickedPcfCalculationOutcome>> {
  try {
    const stream = context.client.calculatePickedLocalPcf({
      runId: context.runId,
      traceId: context.traceId,
      grouping: mapGrouping(options.groupBy),
      settings: context.settings.grpc,
    }, {
      headers: { traceparent: context.traceparent },
    });
    const value = await consumeStream<LocalPickedPcfCalculationResult, LocalPickedPcfCalculationOutcome>(
      stream,
      options.onProgress,
      (terminal) => normalizePickedCalculation(terminal, context),
    );
    return { ok: true, value };
  } catch (error) {
    return { ok: false, failure: calculationFailure(context, error, true) };
  }
}
